import { Script, graphql } from "gatsby"
import React from "react"
import { Layout } from "@bw/layouts"
import { Seo, Section, CmsImage } from "@bw/bits"
import { Content, Header } from "../components/modules/mapModules.jsx"
import { Teasers, SearchPharmacy } from "@bw/modules"
import { Teaser, Breadcrumbs } from "@bw/partials"
import { useTranslation } from "react-i18next"
import shuffle from "../utils/shuffle"

export const Head = ({ data }) => (
  <>
    {data.collectionPrestations.head && (
      <Script
        id={`user-script-${data.collectionPrestations.id}`}
        dangerouslySetInnerHTML={{ __html: data.collectionPrestations.head }}
      />
    )}
  </>
)

const PrestationTemplate = ({ data, pageContext, location }) => {
  const { collectionPrestations, prestations, pharmacies, mount } = data
  const prestation = collectionPrestations
  const allPrestations = shuffle(prestations.nodes).slice(0, 3)
  const { t } = useTranslation()
  const breadcrumbsLinks = [
    {
      title: "Accueil",
      to: "/",
    },
    {
      title: mount.title,
      to: mount.slug,
    },
    {
      title: prestation.title,
      to: prestation.slug,
    },
  ]

  /**
   * Find pharmacies that offer this prestation
   */
  let participating = []
  if (pharmacies?.nodes) {
    participating = pharmacies.nodes
      .filter(pharmacy => {
        return pharmacy.prestations.map(p => p.id).includes(pageContext.id)
      })
      .map(p => ({
        ...p,
        state: p.state?.value,
      }))
  }

  return (
    <Layout color={prestation.color?.value} {...{ pageContext }}>
      <Seo
        url={location.href}
        title={prestation.metaTitle || prestation.title}
        description={prestation.metaDescription}
        image={prestation.image?.url}
        meta={[
          {
            name: "robots",
            content: prestation.doNotIndex
              ? "noindex, nofollow"
              : "max-image-preview:large, index, follow",
          },
        ]}
      />
      <Header modules={prestation.header} page={prestation} />
      {breadcrumbsLinks && <Breadcrumbs breadcrumbs={breadcrumbsLinks} />}
      <Content modules={prestation.content} />
      {participating && participating.length > 0 && (
        <Section margin="100px 0" title={t("participatingPharmacies")}>
          <SearchPharmacy
            pharmacies={participating}
            map={false}
            textSearch={false}
          />
        </Section>
      )}
      <Section margin="100px 0" title={t("relatedContent")}>
        <Teasers>
          {allPrestations.map((item, i) => {
            return (
              <Teaser
                smaller
                key={i}
                title={item.title}
                excerpt={item.teaserExcerpt}
                to={item.slug || item.uri}
                image={<CmsImage src={item.teaserImage} alt={item.title} />}
              />
            )
          })}
        </Teasers>
      </Section>
    </Layout>
  )
}

export default PrestationTemplate

export const prestationQuery = graphql`
  query prestationQuery($id: String!) {
    collectionPrestations(id: { eq: $id }) {
      id
      head
      slug
      title
      pictogram {
        value
      }
      metaDescription
      image {
        url
      }
      metaTitle
      doNotIndex
      color {
        value
      }
      teaserExcerpt
      teaserImage {
        localImage {
          childImageSharp {
            gatsbyImageData(width: 254, height: 139)
          }
        }
      }
      header {
        __typename
        suptitle
        title
        subtitle

        ... on PageHeaderModule {
          ...PageHeaderModuleFragment
        }
        ... on HeroModule {
          ...HeroModuleFragment
        }
      }
      content {
        __typename
        suptitle
        title
        subtitle
        buttonType {
          value
        }
        buttonUrl
        buttonIsExternal
        buttonText
        buttonPosition {
          value
        }
        buttonEntry {
          url
        }
        ancre

        ... on CallToActionModule {
          ...CallToActionModuleFragment
        }
        ... on ContentModule {
          ...ContentModuleFragment
        }
        ... on ContentWithImageModule {
          ...ContentWithImageModuleFragment
        }
        ... on GalleryModule {
          ...GalleryModuleFragment
        }
        ... on ContentOnColumnsModule {
          ...ContentOnColumnsModuleFragment
        }
        ... on TeasersLatestModule {
          ...TeasersLatestModuleFragment
        }
        ... on LogoGridModule {
          ...LogoGridModuleFragment
        }
        ... on ImageModule {
          ...ImageModuleFragment
        }
        ... on VideoModule {
          ...VideoModuleFragment
        }
        ... on DownloadsModule {
          ...DownloadsModuleFragment
        }
      }
    }
    pharmacies: allCollectionPharmacies {
      nodes {
        id
        prestations {
          id
        }
        title
        slug
        street
        zipCode
        city
        state {
          value
        }
        latitude
        longitude
      }
    }
    prestations: allCollectionPrestations(filter: { id: { ne: $id } }) {
      nodes {
        title
        teaserExcerpt
        slug
        teaserImage {
          localImage {
            childImageSharp {
              gatsbyImageData(width: 351, height: 175)
            }
          }
        }
      }
    }
    mount: collectionPages(mount: { handle: { eq: "prestations" } }) {
      slug
      title
      header {
        ... on PageHeaderModule {
          image {
            url
          }
        }
      }
    }
  }
`
